// Multi-step CV Builder with autosave + live preview. Ships with all 10 sections A–J.
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import {
  User, FileText, Briefcase, GraduationCap, Wrench, Award, Languages as LanguagesIcon,
  Lightbulb, PlusCircle, Settings2, ChevronLeft, ChevronRight, Check, Save, Eye, EyeOff,
  Download, Trash2, Sparkles, AlertCircle, Loader2, X, LayoutTemplate
} from "lucide-react";
import TemplateRenderer from "../components/TemplateRenderer";
import { findTemplate, DEFAULT_TEMPLATE_ID } from "../lib/templates";

const emptyData = () => ({
  personal: { full_name: "", headline: "", email: "", phone: "", location: "", linkedin: "", website: "", photo_url: "", nationality: "", notice_period: "" },
  summary: { text: "", years_experience: "", industry: "", strengths: "", target_role: "" },
  experience: [],
  education: [],
  skills: { technical: [], soft: [], tools: [], with_levels: [] },
  certifications: [],
  languages: [],
  projects: [],
  additional: [],
  settings: { target_role: "", industry: "", market: "Global", length: "One page", template_id: "cavilow-classic", ats_friendly: true, show_photo: false, hidden_sections: [] },
});

const STEPS = [
  { id: "personal", label: "Personal", icon: User },
  { id: "summary", label: "Summary", icon: FileText },
  { id: "experience", label: "Experience", icon: Briefcase },
  { id: "education", label: "Education", icon: GraduationCap },
  { id: "skills", label: "Skills", icon: Wrench },
  { id: "certifications", label: "Certifications", icon: Award },
  { id: "languages", label: "Languages", icon: LanguagesIcon },
  { id: "projects", label: "Projects", icon: Lightbulb },
  { id: "additional", label: "Additional", icon: PlusCircle },
  { id: "settings", label: "Settings", icon: Settings2 },
];

export default function CvBuilder() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { cvId } = useParams();
  const isPremium = user?.subscription === "premium" || user?.is_admin;

  const [data, setData] = useState(emptyData());
  const [currentCvId, setCurrentCvId] = useState(cvId || null);
  const [step, setStep] = useState(0);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showPreview, setShowPreview] = useState(false); // mobile toggle
  const [showLimitModal, setShowLimitModal] = useState(false);
  const saveTimer = useRef(null);
  const firstLoad = useRef(true);

  // Load existing CV or create new draft
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (cvId) {
          const r = await api.get(`/cv/${cvId}`);
          if (!cancelled) {
            setData({ ...emptyData(), ...(r.data.data || {}) });
            setCurrentCvId(cvId);
          }
        } else {
          // Try to load user's most recent CV (so free users always land on their existing CV)
          const list = await api.get("/cv/list");
          const items = list.data.items || [];
          if (items.length > 0 && !isPremium) {
            // Free user: auto-open their existing CV
            const doc = items[0];
            const full = await api.get(`/cv/${doc.cv_id}`);
            if (!cancelled) {
              setData({ ...emptyData(), ...(full.data.data || {}) });
              setCurrentCvId(doc.cv_id);
              nav(`/cv-builder/${doc.cv_id}`, { replace: true });
            }
          } else {
            // Premium (or free with 0 CVs) → create a new draft
            try {
              const r = await api.post("/cv/draft", { data: emptyData() });
              if (!cancelled) {
                setCurrentCvId(r.data.cv_id);
                nav(`/cv-builder/${r.data.cv_id}`, { replace: true });
              }
            } catch (err) {
              if (err?.response?.status === 402) {
                setShowLimitModal(true);
              }
            }
          }
        }
      } catch {
        toast.error("Failed to load CV");
      } finally {
        if (!cancelled) {
          setLoading(false);
          firstLoad.current = false;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [cvId]);

  // Autosave (debounced)
  useEffect(() => {
    if (firstLoad.current || !currentCvId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        setSaving(true);
        await api.patch(`/cv/${currentCvId}`, { data });
        setSavedAt(new Date());
      } catch {
        // silent
      } finally {
        setSaving(false);
      }
    }, 900);
    return () => saveTimer.current && clearTimeout(saveTimer.current);
  }, [data, currentCvId]);

  const updateSection = (key, val) => setData((d) => ({ ...d, [key]: val }));

  const download = async (fmt = "pdf") => {
    if (!currentCvId) return;
    if (fmt === "docx" && !isPremium) {
      setShowLimitModal(true);
      return;
    }
    try {
      const resp = await api.post("/cv/export", { cv_id: currentCvId, format: fmt }, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(data.personal?.full_name || "cavilow-cv").replace(/\s+/g, "_")}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      if (e?.response?.status === 402) {
        setShowLimitModal(true);
      } else {
        toast.error("Download failed");
      }
    }
  };

  if (loading) {
    return (
      <div className="grid place-items-center py-24" data-testid="cv-builder-loading">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  const StepComp = SECTIONS[STEPS[step].id];
  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="space-y-6" data-testid="cv-builder">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 fade-up">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#80FFEA]">Resume Builder</p>
          <h1 className="font-display font-black text-4xl tracking-tight mt-1">Build your CV, step by step.</h1>
          <p className="text-slate-400 mt-2 max-w-xl text-sm">
            Cavilow autosaves as you type. Preview updates live. Keep it real — Cavilow will not invent experience.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SaveIndicator saving={saving} savedAt={savedAt} />
          <button
            data-testid="cv-preview-toggle"
            onClick={() => setShowPreview((v) => !v)}
            className="lg:hidden inline-flex items-center gap-2 rounded-xl border-2 border-white/10 text-slate-200 px-4 py-2 text-sm font-bold btn-press"
          >
            {showPreview ? <EyeOff size={14} /> : <Eye size={14} />}
            {showPreview ? "Edit" : "Preview"}
          </button>
          <button
            data-testid="cv-download-pdf"
            onClick={() => download("pdf")}
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-4 py-2 text-sm font-bold shadow-lg shadow-blue-500/20 btn-press"
          >
            <Download size={14} /> PDF
          </button>
          <button
            data-testid="cv-download-docx"
            onClick={() => download("docx")}
            className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-white border-2 border-white/10 text-slate-200 hover:border-[var(--cavilo-ink)] px-4 py-2 text-sm font-bold btn-press"
          >
            <Download size={14} /> DOCX {!isPremium && <Sparkles size={12} className="text-amber-500" />}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="glass p-4">
        <div className="flex items-center justify-between mb-2 text-xs">
          <span className="font-bold text-white">Step {step + 1} of {STEPS.length} · {STEPS[step].label}</span>
          <span className="text-slate-400">{progress}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.06] overflow-hidden">
          <div className="h-full bg-[var(--cavilo-blue)] rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
        <div className="mt-4 flex gap-1 overflow-x-auto pb-1">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = i === step;
            const done = i < step;
            return (
              <button
                key={s.id}
                data-testid={`cv-step-${s.id}`}
                onClick={() => setStep(i)}
                className={`flex-1 min-w-[86px] rounded-2xl p-2.5 flex flex-col items-center gap-1 border-2 transition-colors ${
                  active
                    ? "border-[var(--cavilo-blue)] bg-[rgba(157,0,255,0.14)] text-[#80FFEA]"
                    : done
                      ? "border-emerald-100 bg-[rgba(128,255,234,0.1)] text-[#80FFEA]"
                      : "border-transparent text-slate-400 hover:bg-white/5"
                }`}
              >
                {done ? <Check size={16} /> : <Icon size={16} />}
                <span className="text-[10px] font-bold">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content grid */}
      <div className="grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-6">
        {/* Editor */}
        <div className={showPreview ? "hidden lg:block" : "block"}>
          <div className="glass p-6 space-y-5" data-testid={`cv-section-${STEPS[step].id}`}>
            <StepComp data={data} setData={updateSection} isPremium={isPremium} cvId={currentCvId} />
            <div className="flex items-center justify-between pt-4 border-t border-white/8">
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="cv-prev-step"
                className="inline-flex items-center gap-1 rounded-xl border-2 border-white/10 text-slate-300 hover:text-white px-4 py-2 text-sm font-bold disabled:opacity-40"
              >
                <ChevronLeft size={16} /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button
                  onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                  data-testid="cv-next-step"
                  className="inline-flex items-center gap-1 rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-5 py-2 text-sm font-bold btn-press"
                >
                  Next <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={() => download("pdf")}
                  data-testid="cv-finish"
                  className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2 text-sm font-bold btn-press"
                >
                  <Check size={16} /> Finish & Download PDF
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className={showPreview ? "block" : "hidden lg:block"}>
          <div className="sticky top-6">
            <div className="glass shadow-[0_20px_50px_-25px_rgba(15,23,42,0.35)] p-6" data-testid="cv-preview">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Live preview</div>
                <span className="text-[10px] font-bold text-slate-400">{findTemplate(data.settings?.template_id).name}</span>
              </div>
              <TemplateRenderer templateId={data.settings?.template_id || DEFAULT_TEMPLATE_ID} data={data} watermark={!isPremium} />
            </div>
          </div>
        </div>
      </div>

      {showLimitModal && <LimitModal onClose={() => setShowLimitModal(false)} />}
    </div>
  );
}

function SaveIndicator({ saving, savedAt }) {
  const label = saving ? "Saving…" : savedAt ? `Saved ${timeAgo(savedAt)}` : "Not saved yet";
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-400" data-testid="cv-save-indicator">
      {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
      {label}
    </span>
  );
}

function timeAgo(d) {
  const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  const m = Math.floor(diff / 60);
  return `${m}m ago`;
}

/* ---------- Section components ---------- */
const Field = ({ label, hint, children, testid }) => (
  <label className="block" data-testid={testid ? `field-${testid}` : undefined}>
    <span className="block text-xs font-bold text-slate-300 mb-1.5">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-slate-400 mt-1">{hint}</span>}
  </label>
);

const Input = ({ value, onChange, placeholder, type = "text", testid }) => (
  <input
    data-testid={testid}
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full rounded-xl border-2 border-white/10 px-3.5 py-2.5 text-sm outline-none focus:border-[var(--cavilo-blue)] transition-colors"
  />
);

const TextArea = ({ value, onChange, placeholder, rows = 4, testid }) => (
  <textarea
    data-testid={testid}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    rows={rows}
    className="w-full rounded-xl border-2 border-white/10 px-3.5 py-2.5 text-sm outline-none focus:border-[var(--cavilo-blue)] transition-colors resize-y"
  />
);

function PersonalStep({ data, setData }) {
  const p = data.personal || {};
  const upd = (k, v) => setData("personal", { ...p, [k]: v });
  return (
    <>
      <h2 className="font-display font-bold text-xl text-white">A. Personal Details</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name *"><Input testid="personal-name" value={p.full_name} onChange={(v) => upd("full_name", v)} placeholder="Jane Doe" /></Field>
        <Field label="Professional headline / target job title" hint="Shown under your name on the CV.">
          <Input testid="personal-headline" value={p.headline} onChange={(v) => upd("headline", v)} placeholder="Senior Product Designer" />
        </Field>
        <Field label="Email *"><Input testid="personal-email" value={p.email} onChange={(v) => upd("email", v)} placeholder="you@example.com" type="email" /></Field>
        <Field label="Phone"><Input testid="personal-phone" value={p.phone} onChange={(v) => upd("phone", v)} placeholder="+971 50 000 0000" /></Field>
        <Field label="Location"><Input testid="personal-location" value={p.location} onChange={(v) => upd("location", v)} placeholder="Dubai, UAE" /></Field>
        <Field label="LinkedIn URL"><Input testid="personal-linkedin" value={p.linkedin} onChange={(v) => upd("linkedin", v)} placeholder="linkedin.com/in/…" /></Field>
        <Field label="Portfolio / website"><Input testid="personal-website" value={p.website} onChange={(v) => upd("website", v)} placeholder="janedoe.com" /></Field>
        <Field label="Nationality / work authorization (optional)" hint="Common in UAE / GCC applications.">
          <Input testid="personal-nationality" value={p.nationality} onChange={(v) => upd("nationality", v)} placeholder="e.g. UAE Golden Visa" />
        </Field>
        <Field label="Notice period / availability (optional)">
          <Input testid="personal-notice" value={p.notice_period} onChange={(v) => upd("notice_period", v)} placeholder="e.g. 30 days" />
        </Field>
      </div>
    </>
  );
}

function SummaryStep({ data, setData }) {
  const s = data.summary || {};
  const upd = (k, v) => setData("summary", { ...s, [k]: v });
  return (
    <>
      <h2 className="font-display font-bold text-xl text-white">B. Professional Summary</h2>
      <Field label="Professional summary" hint="3–5 lines. Focus on real strengths, not marketing fluff.">
        <TextArea testid="summary-text" value={s.text} onChange={(v) => upd("text", v)} rows={5} placeholder="Product designer with 6 years' experience shipping SaaS…" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Years of experience"><Input testid="summary-years" value={s.years_experience} onChange={(v) => upd("years_experience", v)} placeholder="6" /></Field>
        <Field label="Industry"><Input testid="summary-industry" value={s.industry} onChange={(v) => upd("industry", v)} placeholder="Fintech" /></Field>
        <Field label="Core strengths"><Input testid="summary-strengths" value={s.strengths} onChange={(v) => upd("strengths", v)} placeholder="Design systems, user research…" /></Field>
        <Field label="Target role"><Input testid="summary-target" value={s.target_role} onChange={(v) => upd("target_role", v)} placeholder="Senior Product Designer" /></Field>
      </div>
    </>
  );
}

function ExperienceStep({ data, setData }) {
  const list = data.experience || [];
  const upd = (i, patch) => setData("experience", list.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const add = () => setData("experience", [...list, { id: `exp_${Date.now()}`, title: "", company: "", location: "", employment_type: "Full-time", start_month: "", start_year: "", end_month: "", end_year: "", current: false, achievements: [] }]);
  const del = (i) => setData("experience", list.filter((_, idx) => idx !== i));
  const dup = (i) => setData("experience", [...list.slice(0, i + 1), { ...list[i], id: `exp_${Date.now()}` }, ...list.slice(i + 1)]);
  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl text-white">C. Work Experience</h2>
        <button onClick={add} data-testid="exp-add" className="inline-flex items-center gap-1 text-sm font-bold text-[#80FFEA] hover:text-white">
          <PlusCircle size={16} /> Add role
        </button>
      </div>
      {list.length === 0 && <EmptyState msg="No roles yet. Add your first job." />}
      {list.map((e, i) => (
        <div key={e.id} className="rounded-2xl border-2 border-white/8 p-4 space-y-3" data-testid={`exp-item-${i}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Job title *"><Input testid={`exp-title-${i}`} value={e.title} onChange={(v) => upd(i, { title: v })} placeholder="Product Designer" /></Field>
            <Field label="Company *"><Input testid={`exp-company-${i}`} value={e.company} onChange={(v) => upd(i, { company: v })} placeholder="Acme Corp" /></Field>
            <Field label="Location"><Input value={e.location} onChange={(v) => upd(i, { location: v })} placeholder="Dubai" /></Field>
            <Field label="Employment type">
              <select value={e.employment_type} onChange={(ev) => upd(i, { employment_type: ev.target.value })}
                className="w-full rounded-xl border-2 border-white/10 px-3.5 py-2.5 text-sm outline-none focus:border-[var(--cavilo-blue)] bg-white">
                {["Full-time", "Part-time", "Contract", "Internship", "Freelance", "Temporary"].map((o) => <option key={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Start (month year)">
              <div className="grid grid-cols-2 gap-2">
                <Input value={e.start_month} onChange={(v) => upd(i, { start_month: v })} placeholder="Jan" />
                <Input value={e.start_year} onChange={(v) => upd(i, { start_year: v })} placeholder="2022" />
              </div>
            </Field>
            <Field label="End (month year)">
              {e.current ? (
                <div className="flex items-center h-[42px] px-3.5 rounded-xl border-2 border-white/8 bg-white/5 text-sm text-slate-400">Present</div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Input value={e.end_month} onChange={(v) => upd(i, { end_month: v })} placeholder="Dec" />
                  <Input value={e.end_year} onChange={(v) => upd(i, { end_year: v })} placeholder="2024" />
                </div>
              )}
            </Field>
          </div>
          <label className="inline-flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={!!e.current} onChange={(ev) => upd(i, { current: ev.target.checked })} className="accent-[var(--cavilo-blue)]" />
            Currently working here
          </label>
          <Field label="Responsibilities and achievements" hint="One bullet per line. Use real results.">
            <TextArea rows={5} testid={`exp-bullets-${i}`}
              value={(e.achievements || []).join("\n")}
              onChange={(v) => upd(i, { achievements: v.split("\n") })}
              placeholder={"• Led onboarding redesign that lifted activation 22%\n• Managed 3 designers…"} />
          </Field>
          <div className="flex gap-2 text-xs">
            <button onClick={() => dup(i)} className="text-slate-400 hover:text-white font-bold">Duplicate</button>
            <button onClick={() => del(i)} className="text-rose-500 hover:text-rose-700 font-bold inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
          </div>
        </div>
      ))}
    </>
  );
}

function EducationStep({ data, setData }) {
  const list = data.education || [];
  const upd = (i, patch) => setData("education", list.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const add = () => setData("education", [...list, { id: `edu_${Date.now()}`, degree: "", institution: "", location: "", start_year: "", end_year: "", grade: "", details: "" }]);
  const del = (i) => setData("education", list.filter((_, idx) => idx !== i));
  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl text-white">D. Education</h2>
        <button onClick={add} data-testid="edu-add" className="inline-flex items-center gap-1 text-sm font-bold text-[#80FFEA]"><PlusCircle size={16} /> Add education</button>
      </div>
      {list.length === 0 && <EmptyState msg="No education yet." />}
      {list.map((e, i) => (
        <div key={e.id} className="rounded-2xl border-2 border-white/8 p-4 space-y-3" data-testid={`edu-item-${i}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Degree"><Input testid={`edu-degree-${i}`} value={e.degree} onChange={(v) => upd(i, { degree: v })} placeholder="B.Sc. Computer Science" /></Field>
            <Field label="Institution"><Input testid={`edu-inst-${i}`} value={e.institution} onChange={(v) => upd(i, { institution: v })} placeholder="Delhi University" /></Field>
            <Field label="Location"><Input value={e.location} onChange={(v) => upd(i, { location: v })} placeholder="New Delhi" /></Field>
            <Field label="Grade / GPA (optional)"><Input value={e.grade} onChange={(v) => upd(i, { grade: v })} placeholder="8.4 / 10" /></Field>
            <Field label="Start year"><Input value={e.start_year} onChange={(v) => upd(i, { start_year: v })} placeholder="2018" /></Field>
            <Field label="End year"><Input value={e.end_year} onChange={(v) => upd(i, { end_year: v })} placeholder="2022" /></Field>
          </div>
          <Field label="Additional details (optional)"><TextArea rows={2} value={e.details} onChange={(v) => upd(i, { details: v })} placeholder="Thesis, honors, activities…" /></Field>
          <button onClick={() => del(i)} className="text-xs text-rose-500 font-bold inline-flex items-center gap-1"><Trash2 size={12} /> Remove</button>
        </div>
      ))}
    </>
  );
}

function TagInput({ items, onChange, placeholder, testid }) {
  const [text, setText] = useState("");
  const commit = () => {
    const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
    if (parts.length) onChange([...(items || []), ...parts]);
    setText("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2" data-testid={testid ? `${testid}-tags` : undefined}>
        {(items || []).map((t, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-[rgba(157,0,255,0.14)] text-[#80FFEA] px-2.5 py-1 text-xs font-bold">
            {t}
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="hover:text-white" aria-label="Remove">
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          data-testid={testid}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); } }}
          placeholder={placeholder}
          className="flex-1 rounded-xl border-2 border-white/10 px-3.5 py-2.5 text-sm outline-none focus:border-[var(--cavilo-blue)]"
        />
        <button onClick={commit} className="rounded-xl bg-white/[0.06] hover:bg-white/10 px-3 text-sm font-bold text-slate-200">Add</button>
      </div>
    </div>
  );
}

function SkillsStep({ data, setData }) {
  const s = data.skills || {};
  const upd = (k, v) => setData("skills", { ...s, [k]: v });
  return (
    <>
      <h2 className="font-display font-bold text-xl text-white">E. Skills</h2>
      <Field label="Technical skills" hint="Press Enter or comma to add.">
        <TagInput testid="skills-tech" items={s.technical} onChange={(v) => upd("technical", v)} placeholder="Figma, Framer, SQL…" />
      </Field>
      <Field label="Soft skills"><TagInput testid="skills-soft" items={s.soft} onChange={(v) => upd("soft", v)} placeholder="Communication, Ownership…" /></Field>
      <Field label="Tools / software"><TagInput testid="skills-tools" items={s.tools} onChange={(v) => upd("tools", v)} placeholder="Jira, Notion, Slack…" /></Field>
    </>
  );
}

function CertsStep({ data, setData }) {
  const list = data.certifications || [];
  const upd = (i, p) => setData("certifications", list.map((e, idx) => (idx === i ? { ...e, ...p } : e)));
  const add = () => setData("certifications", [...list, { id: `cert_${Date.now()}`, name: "", issuer: "", issue_date: "", expiry_date: "", credential_url: "" }]);
  const del = (i) => setData("certifications", list.filter((_, idx) => idx !== i));
  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl text-white">F. Certifications</h2>
        <button onClick={add} data-testid="cert-add" className="inline-flex items-center gap-1 text-sm font-bold text-[#80FFEA]"><PlusCircle size={16} /> Add certification</button>
      </div>
      {list.length === 0 && <EmptyState msg="No certifications yet." />}
      {list.map((c, i) => (
        <div key={c.id} className="rounded-2xl border-2 border-white/8 p-4 space-y-3" data-testid={`cert-item-${i}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Certification name"><Input testid={`cert-name-${i}`} value={c.name} onChange={(v) => upd(i, { name: v })} placeholder="AWS Solutions Architect" /></Field>
            <Field label="Issuing organization"><Input value={c.issuer} onChange={(v) => upd(i, { issuer: v })} placeholder="Amazon" /></Field>
            <Field label="Issue date"><Input value={c.issue_date} onChange={(v) => upd(i, { issue_date: v })} placeholder="Feb 2025" /></Field>
            <Field label="Expiry date (optional)"><Input value={c.expiry_date} onChange={(v) => upd(i, { expiry_date: v })} placeholder="Feb 2028" /></Field>
            <Field label="Credential URL (optional)"><Input value={c.credential_url} onChange={(v) => upd(i, { credential_url: v })} placeholder="https://…" /></Field>
          </div>
          <button onClick={() => del(i)} className="text-xs text-rose-500 font-bold"><Trash2 size={12} className="inline" /> Remove</button>
        </div>
      ))}
    </>
  );
}

function LanguagesStep({ data, setData }) {
  const list = data.languages || [];
  const upd = (i, p) => setData("languages", list.map((e, idx) => (idx === i ? { ...e, ...p } : e)));
  const add = () => setData("languages", [...list, { id: `lang_${Date.now()}`, name: "", proficiency: "Fluent" }]);
  const del = (i) => setData("languages", list.filter((_, idx) => idx !== i));
  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl text-white">G. Languages</h2>
        <button onClick={add} data-testid="lang-add" className="inline-flex items-center gap-1 text-sm font-bold text-[#80FFEA]"><PlusCircle size={16} /> Add language</button>
      </div>
      {list.length === 0 && <EmptyState msg="No languages yet." />}
      {list.map((l, i) => (
        <div key={l.id} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end rounded-2xl border-2 border-white/8 p-4" data-testid={`lang-item-${i}`}>
          <Field label="Language"><Input testid={`lang-name-${i}`} value={l.name} onChange={(v) => upd(i, { name: v })} placeholder="Arabic" /></Field>
          <Field label="Proficiency">
            <select value={l.proficiency} onChange={(e) => upd(i, { proficiency: e.target.value })}
              className="w-full rounded-xl border-2 border-white/10 px-3.5 py-2.5 text-sm outline-none focus:border-[var(--cavilo-blue)] bg-white">
              {["Native", "Fluent", "Advanced", "Intermediate", "Basic"].map((o) => <option key={o}>{o}</option>)}
            </select>
          </Field>
          <button onClick={() => del(i)} className="text-xs text-rose-500 font-bold h-[42px]"><Trash2 size={14} /></button>
        </div>
      ))}
    </>
  );
}

function ProjectsStep({ data, setData }) {
  const list = data.projects || [];
  const upd = (i, p) => setData("projects", list.map((e, idx) => (idx === i ? { ...e, ...p } : e)));
  const add = () => setData("projects", [...list, { id: `proj_${Date.now()}`, title: "", role: "", description: "", tools: "", link: "" }]);
  const del = (i) => setData("projects", list.filter((_, idx) => idx !== i));
  return (
    <>
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl text-white">H. Projects</h2>
        <button onClick={add} data-testid="proj-add" className="inline-flex items-center gap-1 text-sm font-bold text-[#80FFEA]"><PlusCircle size={16} /> Add project</button>
      </div>
      {list.length === 0 && <EmptyState msg="No projects yet." />}
      {list.map((p, i) => (
        <div key={p.id} className="rounded-2xl border-2 border-white/8 p-4 space-y-3" data-testid={`proj-item-${i}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Project title"><Input testid={`proj-title-${i}`} value={p.title} onChange={(v) => upd(i, { title: v })} placeholder="Cavilow launch" /></Field>
            <Field label="Your role"><Input value={p.role} onChange={(v) => upd(i, { role: v })} placeholder="Product lead" /></Field>
            <Field label="Tools used"><Input value={p.tools} onChange={(v) => upd(i, { tools: v })} placeholder="Figma, Notion, Linear" /></Field>
            <Field label="Link (optional)"><Input value={p.link} onChange={(v) => upd(i, { link: v })} placeholder="https://…" /></Field>
          </div>
          <Field label="Description"><TextArea rows={3} value={p.description} onChange={(v) => upd(i, { description: v })} /></Field>
          <button onClick={() => del(i)} className="text-xs text-rose-500 font-bold"><Trash2 size={12} className="inline" /> Remove</button>
        </div>
      ))}
    </>
  );
}

function AdditionalStep({ data, setData }) {
  const list = data.additional || [];
  const upd = (i, p) => setData("additional", list.map((e, idx) => (idx === i ? { ...e, ...p } : e)));
  const add = (type) => setData("additional", [...list, { id: `add_${Date.now()}`, type, title: type[0].toUpperCase() + type.slice(1), content: "" }]);
  const del = (i) => setData("additional", list.filter((_, idx) => idx !== i));
  return (
    <>
      <h2 className="font-display font-bold text-xl text-white">I. Additional Sections</h2>
      <div className="flex flex-wrap gap-2">
        {["award", "volunteer", "publication", "membership", "reference", "custom"].map((t) => (
          <button key={t} data-testid={`add-${t}`} onClick={() => add(t)} className="rounded-full border-2 border-white/10 text-slate-300 hover:border-[var(--cavilo-blue)] hover:text-[#80FFEA] px-3 py-1.5 text-xs font-bold capitalize">
            + {t}
          </button>
        ))}
      </div>
      {list.length === 0 && <EmptyState msg="Optional. Add awards, volunteer work, publications, references, or a custom section." />}
      {list.map((a, i) => (
        <div key={a.id} className="rounded-2xl border-2 border-white/8 p-4 space-y-3" data-testid={`add-item-${i}`}>
          <Field label={`Section title (${a.type})`}><Input value={a.title} onChange={(v) => upd(i, { title: v })} placeholder="e.g. Volunteer" /></Field>
          <Field label="Content"><TextArea rows={4} value={a.content} onChange={(v) => upd(i, { content: v })} /></Field>
          <button onClick={() => del(i)} className="text-xs text-rose-500 font-bold"><Trash2 size={12} className="inline" /> Remove</button>
        </div>
      ))}
    </>
  );
}

function SettingsStep({ data, setData, cvId }) {
  const s = data.settings || {};
  const upd = (k, v) => setData("settings", { ...s, [k]: v });
  const tplLink = cvId ? `/resume-templates?cvId=${cvId}` : "/resume-templates";
  return (
    <>
      <h2 className="font-display font-bold text-xl text-white">J. CV Settings</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Target job role"><Input value={s.target_role} onChange={(v) => upd("target_role", v)} placeholder="Senior Product Designer" /></Field>
        <Field label="Industry"><Input value={s.industry} onChange={(v) => upd("industry", v)} placeholder="Fintech" /></Field>
        <Field label="Preferred market">
          <select value={s.market} onChange={(e) => upd("market", e.target.value)}
            className="w-full rounded-xl border-2 border-white/10 px-3.5 py-2.5 text-sm bg-white focus:border-[var(--cavilo-blue)] outline-none">
            {["Global", "UAE / GCC", "India", "United States", "United Kingdom", "Europe"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Resume length">
          <select value={s.length} onChange={(e) => upd("length", e.target.value)}
            className="w-full rounded-xl border-2 border-white/10 px-3.5 py-2.5 text-sm bg-white focus:border-[var(--cavilo-blue)] outline-none">
            {["One page", "Two pages"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </Field>
        <Field label="Template" hint="Change template anytime — your CV data is preserved.">
          <div className="flex items-center gap-3 rounded-xl border-2 border-white/10 px-3.5 py-2.5">
            <span className="w-4 h-4 rounded-full" style={{ background: findTemplate(s.template_id).accent }} />
            <span className="text-sm font-bold text-white flex-1">{findTemplate(s.template_id).name}</span>
            <Link to={tplLink} className="text-xs font-bold text-[#80FFEA] hover:text-white inline-flex items-center gap-1" data-testid="cv-browse-templates">
              <LayoutTemplate size={12} /> Browse all
            </Link>
          </div>
        </Field>
      </div>
      <div className="space-y-3">
        <label className="flex items-start gap-3 rounded-2xl border-2 border-white/8 p-4 cursor-pointer">
          <input type="checkbox" checked={!!s.ats_friendly} onChange={(e) => upd("ats_friendly", e.target.checked)} className="mt-1 accent-[var(--cavilo-blue)]" />
          <span>
            <span className="block text-sm font-bold">ATS-friendly formatting</span>
            <span className="block text-xs text-slate-400 mt-0.5">Recommended for most job applications.</span>
          </span>
        </label>
        <label className="flex items-start gap-3 rounded-2xl border-2 border-white/8 p-4 cursor-pointer">
          <input type="checkbox" checked={!!s.show_photo} onChange={(e) => upd("show_photo", e.target.checked)} className="mt-1 accent-[var(--cavilo-blue)]" />
          <span>
            <span className="block text-sm font-bold">Show professional photo</span>
            <span className="block text-xs text-slate-400 mt-0.5">Photos are optional and may not be suitable for ATS applications in some markets (US/UK). Common in UAE/GCC/EU.</span>
          </span>
        </label>
      </div>
    </>
  );
}

const SECTIONS = {
  personal: PersonalStep,
  summary: SummaryStep,
  experience: ExperienceStep,
  education: EducationStep,
  skills: SkillsStep,
  certifications: CertsStep,
  languages: LanguagesStep,
  projects: ProjectsStep,
  additional: AdditionalStep,
  settings: SettingsStep,
};

function EmptyState({ msg }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-white/10 p-6 text-center text-sm text-slate-400">
      <AlertCircle size={20} className="mx-auto text-slate-400 mb-1.5" />
      {msg}
    </div>
  );
}

/* ---------- Live preview ---------- */
function LivePreview({ data, watermark }) {
  const p = data.personal || {};
  const s = data.summary || {};
  const exp = data.experience || [];
  const edu = data.education || [];
  const sk = data.skills || {};
  const certs = data.certifications || [];
  const langs = data.languages || [];
  const projects = data.projects || [];
  const addl = data.additional || [];

  const contact = [p.email, p.phone, p.location, p.linkedin, p.website].filter(Boolean).join(" · ");
  const monthYear = (m, y) => [m, y].filter(Boolean).join(" ");

  return (
    <div className="cv-preview font-[Manrope,ui-sans-serif,system-ui] text-[13px] text-white leading-[1.55] max-h-[720px] overflow-y-auto pr-2" data-testid="cv-preview-body">
      <div className="pb-3 border-b border-white/10">
        <div className="text-[26px] font-black tracking-tight text-white">{p.full_name || "Your name"}</div>
        {p.headline && <div className="text-sm text-[#80FFEA] font-bold mt-0.5">{p.headline}</div>}
        {contact && <div className="text-[11px] text-slate-400 mt-1">{contact}</div>}
      </div>

      {s.text && (
        <PreviewSection title="Summary">
          <p className="whitespace-pre-wrap">{s.text}</p>
        </PreviewSection>
      )}

      {exp.length > 0 && (
        <PreviewSection title="Experience">
          {exp.map((e) => (
            <div key={e.id} className="mb-3">
              <div className="font-bold text-white">{e.title || "Role"} <span className="font-normal text-slate-400">· {e.company || "Company"}</span></div>
              <div className="text-[11px] text-slate-400">
                {[e.location, `${monthYear(e.start_month, e.start_year)} – ${e.current ? "Present" : monthYear(e.end_month, e.end_year)}`].filter((x) => x && x !== " – ").join(" · ")}
              </div>
              {e.achievements?.filter(Boolean).length > 0 && (
                <ul className="mt-1 pl-4 list-disc marker:text-slate-300">
                  {e.achievements.filter(Boolean).map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              )}
            </div>
          ))}
        </PreviewSection>
      )}

      {edu.length > 0 && (
        <PreviewSection title="Education">
          {edu.map((e) => (
            <div key={e.id} className="mb-2">
              <div className="font-bold text-white">{e.degree || "Degree"}</div>
              <div className="text-[11px] text-slate-400">
                {[e.institution, e.location, [e.start_year, e.end_year].filter(Boolean).join(" – ")].filter(Boolean).join(" · ")}
              </div>
              {e.grade && <div className="text-[11px]">Grade: {e.grade}</div>}
              {e.details && <div className="text-[12px] mt-0.5">{e.details}</div>}
            </div>
          ))}
        </PreviewSection>
      )}

      {(sk.technical?.length || sk.soft?.length || sk.tools?.length) > 0 && (
        <PreviewSection title="Skills">
          {sk.technical?.length > 0 && <div className="mb-1"><b>Technical:</b> {sk.technical.join(", ")}</div>}
          {sk.soft?.length > 0 && <div className="mb-1"><b>Soft:</b> {sk.soft.join(", ")}</div>}
          {sk.tools?.length > 0 && <div><b>Tools:</b> {sk.tools.join(", ")}</div>}
        </PreviewSection>
      )}

      {certs.length > 0 && (
        <PreviewSection title="Certifications">
          <ul className="pl-4 list-disc marker:text-slate-300">
            {certs.map((c) => <li key={c.id}>{[c.name, c.issuer, c.issue_date].filter(Boolean).join(" · ")}</li>)}
          </ul>
        </PreviewSection>
      )}

      {langs.length > 0 && (
        <PreviewSection title="Languages">
          {langs.map((l) => `${l.name}${l.proficiency ? ` (${l.proficiency})` : ""}`).join(", ")}
        </PreviewSection>
      )}

      {projects.length > 0 && (
        <PreviewSection title="Projects">
          {projects.map((pr) => (
            <div key={pr.id} className="mb-2">
              <div className="font-bold text-white">{pr.title}</div>
              {pr.role && <div className="text-[11px] text-slate-400">{pr.role}</div>}
              {pr.description && <div className="text-[12px]">{pr.description}</div>}
              {pr.tools && <div className="text-[11px] text-slate-400">Tools: {pr.tools}</div>}
            </div>
          ))}
        </PreviewSection>
      )}

      {addl.map((a) => (
        <PreviewSection key={a.id} title={a.title}>
          <div className="whitespace-pre-wrap">{a.content}</div>
        </PreviewSection>
      ))}

      {watermark && (
        <div className="mt-6 pt-3 border-t border-white/8 text-center text-[10px] text-slate-400 italic">
          Created with Cavilow — cavilow.com
        </div>
      )}
    </div>
  );
}

function PreviewSection({ title, children }) {
  return (
    <div className="mt-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#80FFEA] mb-1.5">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function LimitModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center px-4 py-8" onClick={onClose} data-testid="cv-limit-modal">
      <div className="absolute inset-0 bg-[var(--cavilo-ink)]/60 backdrop-blur-sm" />
      <div className="relative bg-white rounded-3xl shadow-2xl border border-white/10 max-w-md w-full p-7 fade-up" onClick={(e) => e.stopPropagation()}>
        <span className="grid place-items-center w-14 h-14 rounded-2xl bg-amber-100 text-amber-600 mb-4">
          <Sparkles size={26} />
        </span>
        <h3 className="font-display font-black text-2xl text-white tracking-tight">
          Create tailored CV versions for different roles with Cavilow Premium.
        </h3>
        <p className="mt-2 text-sm text-slate-300 leading-relaxed">
          Free plan includes 1 CV. Upgrade to Premium for unlimited CVs, DOCX/Word exports, and downloads without a watermark.
        </p>
        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="flex-1 rounded-xl border-2 border-white/10 text-slate-300 hover:text-white px-4 py-3 text-sm font-bold" data-testid="cv-limit-modal-close">
            Maybe later
          </button>
          <Link to="/pricing" className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--cavilo-blue)] hover:bg-[var(--cavilo-blue-deep)] text-white px-4 py-3 text-sm font-bold btn-press" data-testid="cv-limit-modal-upgrade">
            <Sparkles size={14} /> Take the premium plan
          </Link>
        </div>
      </div>
    </div>
  );
}
